/**
 * Pure helpers for the trigger-based upload feature.
 *
 * All three functions are side-effect-free and safe to memoize.
 * Configuration is passed in — these functions don't import the config
 * directly so tests can swap rules/access freely.
 */

/**
 * Decide whether a user is allowed to use trigger-based upload unlock.
 *
 * Rules:
 *   - When neither allowedDomains nor allowedEmails has any entries (or
 *     the access config is null/undefined), everyone is allowed.
 *   - Otherwise the user's email must appear in `allowedEmails` (exact,
 *     case-insensitive) or its domain in `allowedDomains` (case-insensitive).
 *   - A user without an email cannot match a whitelist.
 */
export function isUserAllowed(user, access) {
  const allowedDomains = access?.allowedDomains || [];
  const allowedEmails = access?.allowedEmails || [];

  const noWhitelist = allowedDomains.length === 0 && allowedEmails.length === 0;
  if (noWhitelist) return true;

  if (!user || typeof user.email !== 'string') return false;
  const email = user.email.trim().toLowerCase();
  if (!email) return false;

  if (allowedEmails.some(e => typeof e === 'string' && e.trim().toLowerCase() === email)) {
    return true;
  }

  const domain = email.split('@')[1];
  if (!domain) return false;
  return allowedDomains.some(
    d => typeof d === 'string' && d.trim().toLowerCase() === domain
  );
}

/**
 * Walk the rules in order and return the first whose trigger appears
 * in `text`. Match is **whitespace-bounded** (the trigger must sit between
 * whitespace, string start, or string end) so things like `+include`
 * never accidentally match `+i`.
 *
 * Returns `null` when no rule matches, when `rules` is empty, or when
 * `text` is not a non-empty string.
 */
export function detectTriggerMatch(text, rules) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  if (typeof text !== 'string' || text.length === 0) return null;

  const padded = ` ${text.toLowerCase()} `;
  for (const rule of rules) {
    const triggers = rule?.triggers;
    if (!Array.isArray(triggers)) continue;
    for (const t of triggers) {
      if (typeof t !== 'string' || t.length === 0) continue;
      if (padded.includes(` ${t.toLowerCase()} `)) {
        return rule;
      }
    }
  }
  return null;
}

const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

/**
 * Remove every whitespace-bounded occurrence of any trigger in `rule`
 * from `text`. Resulting whitespace is normalized and trimmed.
 *
 * - Triggers inside other tokens are NOT touched (`+include` stays as-is).
 * - Case-insensitive.
 * - Triggers are sorted longest-first so prefixes (e.g. `+i`) never eat
 *   characters that belong to a longer trigger (`+image`).
 */
export function stripTriggers(text, rule) {
  if (typeof text !== 'string') return text;
  if (!rule || !Array.isArray(rule.triggers) || rule.triggers.length === 0) {
    return text;
  }

  const triggers = rule.triggers
    .filter(t => typeof t === 'string' && t.length > 0)
    .sort((a, b) => b.length - a.length);
  if (triggers.length === 0) return text;

  let padded = ` ${text} `;
  for (const t of triggers) {
    const escaped = t.replace(REGEX_SPECIAL, '\\$&');
    // Consume one leading whitespace + the trigger; lookahead for trailing
    // whitespace so consecutive triggers each get stripped on their own pass.
    const re = new RegExp(`\\s${escaped}(?=\\s)`, 'gi');
    padded = padded.replace(re, ' ');
  }
  return padded.replace(/\s+/g, ' ').trim();
}
